import SkeletonCustom from '@/components/SkeletonCustom';
import { AppPricingPlan, DiscountCodeType, Subscription } from '@/constants/enum';
import { isDowngradePlan } from '@/helpers';
import userPlans from '@/hooks/userPlans';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import { pricingSelector, setPlan, setPricing } from '@/redux/slice/pricing.slice';
import { shopSelector } from '@/redux/slice/shop.slice';
import { IResponseApi } from '@/types/apis/response';
import { Badge, BlockStack, Button, Card, Divider, Icon, InlineGrid, InlineStack, Text } from '@shopify/polaris';
import { CheckIcon } from '@shopify/polaris-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DowngradeModal from '../DowngradeModal/DowngradeModal';
import { PricingOptionStyled } from './styled';
import { DiscountCodeModal } from '../DiscountCodeModal/DiscountCodeModal';
import { calculateDiscountPercentage } from '@/helpers/number';
import { useTranslation } from 'react-i18next';

interface Feature {
  id: string;
  text: string;
}

interface PricingPlan extends IResponseApi.IPricing {
  discountPercent?: number;
  description: string;
  billingPeriod: string;
  popularBadge?: string;
  subtitle: string;
  features: Feature[];
}

const PricingOption: React.FC = () => {
  const dispatch = useDispatch();
  const { isUserFreeLimited, appPlanCurrent } = userPlans();
  const { t } = useTranslation('pricing');

  const isSkip = useSelector(isSkipApiSelector);
  const generalDetailShop = useSelector(shopSelector);
  const pricingState = useSelector(pricingSelector) ?? {};

  const getPlanList = apiCaller.useGetPlansQuery(undefined, { skip: isSkip });
  const [getLinkChargeMutation, getLinkChargeMutationStatus] = apiCaller.useGetLinkChargePlanMutation();

  const plansNonShopifyPlus: PricingPlan[] = useMemo(() => {
    const planPaid = getPlanList.data?.data?.find((plan) => plan?.plan === AppPricingPlan.Paid)!;
    const planPaidCustom: PricingPlan = {
      ...planPaid,
      displayName: 'Paid',
      discountPercent: calculateDiscountPercentage(planPaid?.oldPrice, planPaid?.price),
      billingPeriod: 'monthly',
      popularBadge: t('most_popular'),
      description: t('full_control_unlimited_popups_multilingual_support'),
      subtitle: t('everything_basic_plus'),
      features: [
        { id: 'f1', text: t('text_customization') },
        { id: 'f2', text: t('show_pop_up_all_pages') },
        { id: 'p1', text: t('unlimited_popups_advanced_customization') },
        { id: 'p2', text: t('unlimited_translated_languages') },
        { id: 'p3', text: t('terms_conditions_product_cart_page') },
        { id: 'p4', text: t('live_chat_support_vip_priority') },
      ],
    };
    return [planPaidCustom];
  }, [getPlanList.data?.data, t]);

  const isLoadingGetPlan = getPlanList.isLoading;
  const isLoadingGetLinkCharge = getLinkChargeMutationStatus.isLoading;
  const planOptions: PricingPlan[] = plansNonShopifyPlus;
  const remainTrialDays = Number(generalDetailShop?.remainTrialDays);

  const [isOpenModalDowngradePlan, setIsOpenModalDowngradePlan] = useState(false);
  const [isOpenModalDiscount, setIsOpenModalDiscount] = useState(false);

  const handleGetLinkCharge = async (newPlan: AppPricingPlan) => {
    try {
      const res = await getLinkChargeMutation({ plan: newPlan, subscription: pricingState.subscription });
      if (newPlan !== AppPricingPlan.Free) {
        if ('data' in res && res.data.confirmationUrl) {
          window.open(res.data.confirmationUrl, '_top');
        }
      }
    } catch (err) {
      console.log('Error get link charge', err);
    }
  };

  const handleSelectPlan = async (newPlan: AppPricingPlan) => {
    dispatch(setPlan(newPlan));

    if (isDowngradePlan(appPlanCurrent, newPlan)) {
      setIsOpenModalDowngradePlan(true);
    } else {
      handleGetLinkCharge(newPlan);
    }
  };

  const handleConfirmDowngrade = async () => {
    try {
      await handleGetLinkCharge(pricingState.plan);
      setIsOpenModalDowngradePlan(false);
    } catch (error) {
      console.log('Error handleConfirmDowngrade', error);
    }
  };

  const renderTextButton = (plan: AppPricingPlan) => {
    if (appPlanCurrent === plan) {
      return t('your_current_plan');
    }
    if (remainTrialDays && plan !== AppPricingPlan.Free) {
      return t('start_remain_trial_days_free_trial', { remainTrialDays });
    }
    return t('select_plan');
  };

  useEffect(() => {
    if (generalDetailShop?.appPlan) {
      dispatch(
        setPricing({
          plan: generalDetailShop?.appPlan,
          subscription: generalDetailShop?.subscription || Subscription.Monthly,
          discount: {
            value: getPlanList.data?.discount?.value || 0,
            discountType: getPlanList.data?.discount?.discountType || DiscountCodeType.PERCENTAGE,
            applyFor: getPlanList.data?.discount?.applyFor,
          },
        }),
      );
    }
  }, [
    dispatch,
    generalDetailShop?.appPlan,
    generalDetailShop?.subscription,
    getPlanList.data?.discount?.applyFor,
    getPlanList.data?.discount?.discountType,
    getPlanList.data?.discount?.value,
  ]);

  return (
    <PricingOptionStyled>
      <BlockStack gap="500">
        <BlockStack gap="100"></BlockStack>

        <InlineGrid columns={1} gap="500">
          {planOptions?.map((plan, idx) => (
            <Card padding="600" key={`${plan.plan}-${idx}`}>
              <div className="pricing-item">
                <div className="pricing-content">
                  <InlineStack gap="200" blockAlign="center">
                    {/* Display plan name */}
                    {plan.displayName ? (
                      <Text as="h3" variant="headingMd">
                        {plan.displayName.toUpperCase()}
                      </Text>
                    ) : (
                      <SkeletonCustom height="18px" minWidth="100px" />
                    )}
                    {plan?.popularBadge && <Badge tone="info">{plan.popularBadge}</Badge>}
                  </InlineStack>
                  <Text as="p" tone="subdued">
                    {plan.description}
                  </Text>

                  {/* Display price */}
                  <BlockStack>
                    {plan.price || plan.price === 0 ? (
                      <Text as="h3" variant="heading2xl">
                        ${plan.price}/{t('month')}
                      </Text>
                    ) : (
                      <SkeletonCustom height="24px" minWidth="200px" />
                    )}

                    {plan.discountPercent ? (
                      <Text as="p">
                        {plan.discountPercent}% {t('off')} <span className="old-price">${plan.oldPrice}</span>
                      </Text>
                    ) : null}

                    {plan.plan !== AppPricingPlan.Free && (
                      <Text as="p" tone="subdued">
                        {t('billed_monthly')}
                      </Text>
                    )}
                  </BlockStack>

                  <div className="pricing-content-button">
                    <Button
                      disabled={appPlanCurrent === AppPricingPlan.Paid || isLoadingGetLinkCharge}
                      size="large"
                      variant={appPlanCurrent === plan.plan ? undefined : 'primary'}
                      fullWidth
                      onClick={() => handleSelectPlan(plan.plan)}
                      loading={(isLoadingGetLinkCharge && plan.plan === pricingState.plan) || isLoadingGetPlan}
                    >
                      {renderTextButton(plan.plan)}
                    </Button>
                  </div>
                </div>

                <Divider />

                <div className="pricing-features">
                  {plan.features.map((feature) => (
                    <InlineStack gap="200" blockAlign="start" key={feature.id} wrap={false}>
                      <span className="icon-feature-check">
                        <Icon tone="caution" source={CheckIcon} />
                      </span>
                      <Text as="p" variant="bodyLg">
                        {feature.text}
                      </Text>
                    </InlineStack>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </InlineGrid>

        <Card>
          <InlineStack blockAlign="start" align="space-between">
            <BlockStack gap="100">
              <Text as="h3" variant="headingMd">
                FREE
              </Text>
              <Text as="p" variant="heading2xl">
                $0/{t('month')}
              </Text>
              <Text as="p" tone="subdued">
                {t('testing_maximizing_benefits')}
              </Text>
            </BlockStack>

            <Button
              size="large"
              variant={isUserFreeLimited ? undefined : 'primary'}
              disabled={appPlanCurrent === AppPricingPlan.Free}
              onClick={() => handleSelectPlan(AppPricingPlan.Free)}
            >
              {isUserFreeLimited ? t('your_current_plan') : t('downgrade')}
            </Button>
          </InlineStack>
        </Card>
      </BlockStack>

      {isOpenModalDowngradePlan && (
        <DowngradeModal
          isOpen={isOpenModalDowngradePlan}
          onClose={() => setIsOpenModalDowngradePlan(false)}
          onConfirm={handleConfirmDowngrade}
          isLoading={isLoadingGetLinkCharge}
        />
      )}

      {isOpenModalDiscount && (
        <DiscountCodeModal isOpen={isOpenModalDiscount} handleClose={() => setIsOpenModalDiscount(false)} />
      )}
    </PricingOptionStyled>
  );
};

export default PricingOption;
